import { Suspense, useMemo, useRef, useState, type FC } from 'react';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import type { CheckoutPlan, CheckoutSection } from '../checkoutPlan';
import type { PaymentCallbacks, PaymentContext } from '../paymentTypes';
import type { CheckoutFormProps } from '../providers';
import SectionErrorBoundary from './SectionErrorBoundary';

/**
 * What both checkout layouts (sections, flat list) share: the sections still alive, load-failure
 * removal, each section's PaymentContext with the make_payment guard, and each section's callbacks.
 */
export function useCheckoutSections(plan: CheckoutPlan, paymentContext: PaymentContext, callbacks: PaymentCallbacks) {
  const [failedSectionIds, setFailedSectionIds] = useState<string[]>([]);
  const failedSectionIdsRef = useRef<string[]>([]);
  const sections = useMemo(() => plan.sections.filter((section) => !failedSectionIds.includes(section.id)), [plan, failedSectionIds]);

  // The one section whose make_payment is in flight: no other section starts meanwhile.
  const submittingSectionRef = useRef<string | null>(null);

  const handleLoadError = (section: CheckoutSection, error: unknown) => {
    window.clientLogger?.error?.('checkout section failed to load', { provider: section.provider, role: section.role, error });
    MainTracker.track('custom', 'Checkout Section Load Error', { provider: section.provider, role: section.role, error: (error as any)?.message });
    if (failedSectionIdsRef.current.includes(section.id)) return;
    failedSectionIdsRef.current = [...failedSectionIdsRef.current, section.id];
    setFailedSectionIds(failedSectionIdsRef.current);
    // Nothing left to pay with: the page's error, as when its only checkout fails.
    if (plan.sections.every((candidate) => failedSectionIdsRef.current.includes(candidate.id))) {
      callbacks.onError(error);
    }
  };

  // Per-section context: the section's PSP, and the submit lock.
  const sectionContexts = useMemo(() => Object.fromEntries(plan.sections.map((section) => [section.id, {
    ...paymentContext,
    ...section.contextOverrides,
    onPaymentStart: () => { submittingSectionRef.current = section.id; },
    onPaymentEnd: () => { submittingSectionRef.current = null; },
  } as PaymentContext])), [plan, paymentContext]);

  // Each form fills its own onPaymentAction / onTriggerPaymentSubmit slots, kept across renders;
  // decorate adds layout-specific behaviour on top of the page callbacks.
  const sectionCallbacksRef = useRef<Record<string, PaymentCallbacks>>({});
  const callbacksFor = (section: CheckoutSection, decorate?: (sectionCallbacks: PaymentCallbacks) => PaymentCallbacks): PaymentCallbacks => {
    const previous = sectionCallbacksRef.current[section.id];
    const base: PaymentCallbacks = {
      ...callbacks,
      onPaymentAction: previous?.onPaymentAction ?? (async () => {}),
      onTriggerPaymentSubmit: previous?.onTriggerPaymentSubmit ?? (() => {}),
    };
    const sectionCallbacks = decorate ? decorate(base) : base;
    sectionCallbacksRef.current[section.id] = sectionCallbacks;
    return sectionCallbacks;
  };

  // The page's ID verification modal submits through one section: its callbacks' trigger.
  const triggerSubmitOf = (sectionId: string | undefined) => {
    const active = sectionId ? sectionCallbacksRef.current[sectionId] : undefined;
    if (!active) return false;
    active.isIdVerified = callbacks.isIdVerified;
    active.onTriggerPaymentSubmit?.();
    return true;
  };

  return { sections, handleLoadError, sectionContexts, submittingSectionRef, callbacksFor, triggerSubmitOf };
}

type SectionFormProps = Omit<CheckoutFormProps<unknown>, 'initData'> & {
  section: CheckoutSection;
  // The error boundary's handler; the form gets onLoadError only when the caller passes it.
  onError: (error: unknown) => void;
};

// A section's form, removed by its error boundary when it (or its lazy chunk) fails to load.
export const SectionForm: FC<SectionFormProps> = ({ section, onError, ...props }) => {
  const { Form } = section.adapter;
  return (
    <Suspense fallback={null}>
      <SectionErrorBoundary onError={onError}>
        <Form initData={section.initData} {...props} />
      </SectionErrorBoundary>
    </Suspense>
  );
};
